import { StripeIssuingCardCopyButtonElement } from "@stripe/stripe-js";
import { StaticImageData } from "next/image";
import { JSX } from "react";

//category-type-data
export interface categoryType {
  id: string;
  icon: () => JSX.Element | null;
  title: string;
  desc: string | null;
} 

//category-filter-Type
export interface categoryFilterType {
  id: string;
  category: string;
}

export interface challengeType {
  raceId: string;
  raceTitle: string;
  hostId: string;
  hostName: string;
}

//courses-type
export interface memoCoursesType {
  id: string;
  courseId: string;
  quizId?: string | null;
  title: string;
  description?: string;
  lastUpdated?: string | Date | null;
  allowTrial?: boolean;
  display: boolean;
  videoUrl: string;
  image?: StaticImageData | null;
  totalChapters: number;
  totalQuestions: number;
  courseTypeId?: number;
  automaticValidation?: boolean;
  quizTypeId?: number | string | null;
  classroomJoinModeId?: number | null;
  isClassroomDiscoverable?: boolean | null;
  isPurchasable?: boolean;
  unavailableReason?: string | null;
  localizedAudioCultures?: string[] | null;
  categories: categoryType[];
  tutorImg?: StaticImageData;
  authors: authorType[];
  ratings: ratingType;
  prices?: priceType[];
  localPrices?: priceType[];
  relatedCourses: relatedCoursesType[];
  chapters: chapterType[];
  subType?: SubscriptionTypeEnum;
  isTrialEligible?: boolean;
}

export interface chatType {
  id: number;
  text: string;
  date: Date;
}

export interface summaryType {
  id: string; 
  courseId: string; 
  courseTitle: string;
  quizTypeId: number;
  quizModeId: number;
  studentAnswers: userAnswerType[];
  allowsManualReview?: boolean;
  needsManualReview?: boolean;
  createdDate: Date;
  day: number;
  startTime: Date;
  endTime: Date;
  playTimer: number;
  points: number;
  score: number;
  maxScore: number;
  correctQuestions: number;
  wrongQuestions: number;
  totalQuestions: number;
  avgTimePerQuestion: number;
}

export interface userAnswerType {
  questionId: string;
  question: string;
  requiresManualReview?: boolean;
  isCorrect?: boolean | null;
  score?: number | null;
  answers: partialAnswerType[];
}

export interface partialAnswerType {
  description: string;
  correctAnswer: string;
  isCorrect?: boolean | null;
  score?: number | null;
}

export interface questionType {
  id: string;
  courseId?: string;
  chapterId?: string;
  description: string;
  imageUrl?: string | null;
  imageAssetId?: string | null;
  audioSourceText?: string | null;
  audioAnswerSourceText?: string | null;
  audioAssetId?: string | null;
  audioUrl?: string | null;
  audioStatusId?: number | null;
  audioDurationSeconds?: number | null;
  audioEnabled?: boolean | null;
  answerAudioAssetId?: string | null;
  answerAudioUrl?: string | null;
  answerAudioStatusId?: number | null;
  answerAudioDurationSeconds?: number | null;
  showQuestionText?: boolean | null;
  questionOrder: number;
  questionType: number;
  courseTypeId?: number;
  questionTypeId: number;
  questionSubType: number;
  answers: answerType[];
  questionPart1: string;
  questionPart2: string;
  questionPart3: string;
  isLearned: boolean;
  answerExplained?: string | null;
  AnswerExplained: string | null;
  isValid?: boolean | null;
  errorIdOnCreateQuestion?: number | null;
  hasBeenTried?: boolean;
}

export interface answerType {
  id?: string;
  isCorrect?: boolean;
  description: string;
  answerExplained?: string;
  questionId: string;
  courseId?: string;
  answerOrder?: number;
  questionTypeId?: number;
  markedAsDeleted?: boolean;
}

export interface contentType {
  id: string;
  title: string;
  contentText: string;
  contentTextKind?: "rich_text" | "chess_pgn";
  documentUrl: string;
  assetId?: string;
  contentType: number;
  courseId: string;
  lessonId: string;
  courseTitle: string;
  order?: number;
  checkpointRules?: quizCheckpointRuleType[];
}

export interface quizCheckpointRuleType {
  id: string;
  contentId: string;
  quizId: string;
  quizTitle: string;
  quizDescription?: string | null;
  triggerType: "video-time" | "pdf-page" | string;
  triggerValue: number;
  isBlocking: boolean;
  isEnabled: boolean;
}

export interface leaderboardType {
  id: string;
  courseId: string;
  studentId: string;
  studentName: string;
  score: number;
  country: string;
  correctQuestions: number;
  wrongQuestions: number;
  totalQuestions: number;
  playTimer: number; // in seconds
  createdDate: Date;
}

export interface authorType
{
    id: number;
    name: string;
    tutorImg?: StaticImageData | null;
}

export interface ratingType
{
    ratingAve: number;
    ratingCount: number;
    ratings: number;
}

export interface priceType {
  currency: number;
  price: number;
  oldPrice: number;
  currencySymbol: string;
  discountPercentage: number;
  isoCode: string;
}

export interface gameType {
  answers: answerType[];
  startTime: Date | null;
  endTime: Date | null;
  timer: number | null;
}

//courses-type
export interface relatedCoursesType {
  id: string;
  title: string;
  price: number;
}

export interface dashboardCourseType {
  id: string;
  courseId: string;
  subscriptionId: string;
  courseTypeId: number;
  title: string;
  description: string;
  display: boolean;
  automaticValidation: boolean;
  descriptionTitle: string;
  totalQuestions: number;
  totalChapters: number;
  isQuiz: boolean;
  completedQuestions: number;
  nReviews: number;
  nLearned: number;
  status: number;
  chapters: chapterType[];
  questions: questionType[];
  contents: contentType[];
  lessons: lessonsType[];
  showLearningButton: boolean;
  isArchived: boolean;
  started?: boolean | null;
  transferDate?: string | null;
  transferToUserId?: string | null;
  canTransfer?: boolean;
  subType?: SubscriptionTypeEnum | null;
  subscriptionDate?: string | null;
  quizTypeId: string;
  reviewLevel?: number | null;
  classroomJoinModeId?: number;
  isClassroomDiscoverable?: boolean;
  localizedAudioCultures?: string[] | null;
  requiresCredit?: boolean | null;
  currentLessonTitle?: string | null;
  currentLessonId?: string | null;
  assignedQuizzes?: number | null;
  quizzesDue?: number | null;
  dueQuizzes?: number | null;
  missedQuizzes?: number | null;
  submittedQuizzes?: number | null;
  bestScore?: number | null;
  latestScore?: number | null;
}

export interface tutorCourseType {
  id: string;
  courseId: string;
  title: string;
  description: string;
  descriptionTitle: string;
  totalQuestions: number;
  totalChapters: number;
  totalResource: number;
  status: number;
  chapters: lessonsType[];
  questions: questionType[];
  contents: contentType[];
  lessons: lessonsType[];
  isArchived: boolean;
  isPublished?: boolean | null;
  classroomJoinModeId?: number;
  isClassroomDiscoverable?: boolean;
  localizedAudioCultures?: string[] | null;
  requiresCredit?: boolean | null;
}

export interface quizType {
  id: string;
  name: string;
  description: string;
  quizTypeId: string;
  maxAttempts?: number | string;
  resultsVisibility?: number | string;
  forChildren?: boolean;
}

export interface lessonsType {
  id: string;
  courseId: string;
  courseTitle?: string | null;
  subscriptionId?: string;
  chapterOrder?: number | null;
  isPublished?: boolean | null;
  title: string;
  description: string;
  quizzes: quizType[];
  contents: contentType[];
}

export interface studentClassroomDetailType {
  subscriptionId: string;
  classroom: dashboardCourseType;
  lesson: lessonsType | null;
}

export interface studentClassroomQuizActivityLessonType {
  id: string;
  title: string;
  chapterOrder?: number | null;
}

export interface studentClassroomQuizActivityItemType {
  quizId: string;
  courseId: string;
  chapterId?: string | null;
  quizTitle: string;
  quizDescription?: string | null;
  quizTypeId?: number | null;
  nReviews: number;
  nLearned: number;
  lessonId: string;
  lessonTitle?: string | null;
  contentId?: string | null;
  contentTitle?: string | null;
  assignedAtUtc?: string | null;
  deadlineUtc?: string | null;
  assignmentStatus: number;
  isSubmitted: boolean;
  attemptsCount: number;
  maxAttempts: number;
  remainingAttempts: number;
  canSubmit: boolean;
  bestScore?: number | null;
  latestScore?: number | null;
  firstSubmissionAtUtc?: string | null;
  lastSubmissionAtUtc?: string | null;
  bestScoreId?: string | null;
  latestScoreId?: string | null;
  sourceRoute?: string | null;
}

export interface studentClassroomQuizActivityType {
  subscriptionId: string;
  classroomCourseId: string;
  classroomTitle?: string | null;
  selectedLessonId?: string | null;
  selectedLessonTitle?: string | null;
  lessons: studentClassroomQuizActivityLessonType[];
  quizzes: studentClassroomQuizActivityItemType[];
  totalCount: number;
  assignedCount: number;
  dueCount: number;
  missedCount: number;
  submittedCount: number;
  pendingCount: number;
}

export interface studentClassroomQuizSubmissionItemType {
  summaryId: string;
  submittedAtUtc: string;
  score?: number | null;
  correctQuestions: number;
  totalQuestions: number;
  playTimer?: number | null;
  isBest: boolean;
  isLatest: boolean;
}

export interface studentClassroomQuizSubmissionHistoryType {
  subscriptionId: string;
  lessonId: string;
  quizId: string;
  quizTitle: string;
  attemptsCount: number;
  maxAttempts: number;
  remainingAttempts: number;
  canSubmit: boolean;
  submissions: studentClassroomQuizSubmissionItemType[];
}

export interface tutorClassroomStudentSubmissionItemType {
  summaryId: string;
  submittedAtUtc: string;
  score?: number | null;
  correctQuestions: number;
  totalQuestions: number;
  playTimer?: number | null;
  isBest: boolean;
  isLatest: boolean;
  allowsManualReview?: boolean;
  needsManualReview?: boolean;
}

export interface tutorClassroomStudentQuizSubmissionGroupType {
  quizId: string;
  quizTitle?: string | null;
  lessonId?: string | null;
  lessonTitle?: string | null;
  attemptsCount: number;
  bestScore?: number | null;
  latestScore?: number | null;
  latestSubmittedAtUtc?: string | null;
  submissions: tutorClassroomStudentSubmissionItemType[];
}

export interface tutorClassroomStudentSubmissionHistoryType {
  courseId: string;
  courseTitle?: string | null;
  studentUserId: string;
  studentName?: string | null;
  studentEmail?: string | null;
  quizzes: tutorClassroomStudentQuizSubmissionGroupType[];
}

export interface tutorClassroomStudentAnalyticsItemType {
  userId: string;
  name: string;
  email?: string | null;
  quizzesSubmittedThisWeek: number;
  expectedSubmissions: number;
  submittedSubmissions: number;
  completionRate: number;
  averageScore: number;
  riskLevel: "high" | "medium" | "low";
  status: "needs-attention" | "watch" | "on-track";
}

export interface chapterType {
  id: string;
  title: string;
  description: string;
  chapterOrder?: number | null;
  isArchived: boolean;
  courseTypeId: number;
  quizTypeId?: number | null;
  nReviews: number;
  nLearned: number;
  completedQuestions: number;
  completedPercent: number;
  learnedQuestions: number;
  learnedPercent: number;
  reviewLevel?: number | null;
  toLearn: number;
  totalQuestions: number;
  questions: questionType[];
  courseId: string;
}

export interface questionsFromFileModel {
  subscriptionId: string;
  questions: questionType[];
  isValid: boolean;
  errorIdOnCreateQuestion: number | null;
}

export enum ContentTypeEnum {
  Text,
  Url,
  Maths,
  File,
  Pdf,
}

export enum ExerciseTypeEnum {
  SpacedRepetition = 2,
  Play = 1,
  Standard = 0
}

export enum QuizModeEnum {
  Learn = 0,
  Quiz = 1,
  Play = 2,
}

export enum CourseTypeEnum
{
    TableSquare,
    Geography,
    General,
    Classroom,
  Quiz,
  Alphabet,
  NatoPhoneticAlphabet
}

export enum QuestionTypeEnum {
  TextBox,
  MultipleOption,
  CompletionSentence,
  NumberWithComparisionAnswer,
  TextBoxWithComparisionAnswer,
  SingleOption,
  File,
  TrueFalse,
  FillBlank,
  ChessPgn,
}

export enum QuestionSubTypeEnum {
  MathsMultiplication,
  MathsDivision,
  MathsAddition,
  MathsSubtraction,
}

export enum SubscriptionTypeEnum
    {
        Default = 0,
        Premium,
        Trial,
        TrialExpired
    }

export enum SchoolRoleEnum 
    {
        Student,
        Parent,
        Teacher,
        Tutor,
        School,
        System,
        Claimant
    }

  export enum PlanTierEnum
    {
        Free,
        Standard,
        Premium,
        School
    }    

export interface stripeResponseType {
  id: string;
  amount: number;
  currency: string;
  payment_method: string;
  payment_method_types: string[];
  status: string;
  subscriptionId?: string;
  isRecurring?: boolean;
  card: {
    brand: string;
    last4: string;
    country: string;
  },
  bookingReference: string;
  address_country: string;
  address_postal_code: string;
}